import { createSlice } from '@reduxjs/toolkit';
import { disclaimerModel, funnelType, orderStatus, planModel, rejectionReasonType } from '@repo/ecommerce-api';

export const newOrderSteps = ['order_type', 'customer_data', 'customer_validation', 'order_plan', 'no_offer'];

export interface NameModel {
  firstName: string;
  lastName: string;
  personName?: string;
}
export interface ratesModel {
  quantity: number;
  amount: number;
  expirationDate: string;
  withInterest: boolean;
  planCode: string;
  plan: planModel[];
  disclaimer: disclaimerModel[];
  commercialPlanCode: string;
}

export interface clientDataModel {
  dni?: string;
  sexTypeId?: string;
  personName?: string;
  firstName?: string;
  lastName?: string;
}
export interface commercialPoliciesModel {
  id: number;
  code: string;
  description: string;
  articles: {
    id: number;
    code: string;
    description: string;
  }[];
  products: string[];
}

export type newOrderModel = {
  timestamp: number;
  orderType?: string;
  client: clientDataModel;
  clientOptions: clientDataModel[];
  pendingRequestId?: number;
  minimumAmount?: number;
  maximumAmount?: number;
  requestedAmount: string;
  rates: ratesModel[];
  qr: string;
  redirectUrl: string;
  orderId: string;
  status?: orderStatus;
  selectedDeadline?: string;
  funnel: funnelType[];
  geoLocation: string;
  commercialPolicies: commercialPoliciesModel[];
  commercialPolicyId?: number;
  articleId?: number;
  secondChance?: {
    applyBnpl: boolean;
    bnplApplyAmount: number;
    commercialPolicyId?: number;
    articleId?: number;
    description: string;
  };
  campaignMessage?: string;
  rejectionReason?: rejectionReasonType | null;
};

const initialState: newOrderModel = {
  timestamp: 0,
  orderType: undefined,
  client: {
    dni: '',
    sexTypeId: '',
    personName: '',
    firstName: '',
    lastName: '',
  },
  clientOptions: [{ firstName: '', lastName: '', dni: '', sexTypeId: '' }],
  requestedAmount: '',
  rates: [
    {
      planCode: '',
      quantity: 0,
      amount: 0,
      withInterest: false,
      expirationDate: '',
      plan: [{ expiration: '', quote: '', amount: '' }],
      disclaimer: [{ type: '', content: '' }],
      commercialPlanCode: '',
    },
  ],
  qr: '',
  redirectUrl: '',
  orderId: '',
  funnel: [],
  geoLocation: '',
  commercialPolicies: [
    {
      id: 0,
      code: '',
      description: '',
      articles: [],
      products: [],
    },
  ],
  secondChance: {
    applyBnpl: false,
    bnplApplyAmount: 0,
    commercialPolicyId: 0,
    articleId: 0,
    description: '',
  },
  campaignMessage: '',
  rejectionReason: null,
};

export const newOrderSlice = createSlice({
  name: 'newOrder',
  initialState,
  reducers: {
    pushTimestamp: (state, action: { payload: number }) => ({
      ...state,
      timestamp: action.payload,
    }),
    pushOrderType: (state, action: { payload: string }) => ({
      ...state,
      orderType: action.payload,
    }),
    pushOrderData: (state, action: { payload: newOrderModel }) => ({
      ...state,
      ...action.payload,
    }),
    pushClientData: (state, action: { payload: clientDataModel }) => ({
      ...state,
      client: { ...state.client, ...action.payload },
    }),
    pushPendingRequestId: (state, action: { payload: number }) => ({
      ...state,
      pendingRequestId: action.payload,
    }),
    pushClientOptions: (state, action: { payload: clientDataModel[] }) => ({
      ...state,
      clientOptions: action.payload,
    }),
    pushMinimumAmount: (state, action: { payload: number }) => ({
      ...state,
      minimumAmount: action.payload,
    }),
    pushMaximumAmount: (state, action: { payload: number }) => ({
      ...state,
      maximumAmount: action.payload,
    }),
    pushRequestedAmount: (state, action: { payload: string }) => ({
      ...state,
      requestedAmount: action.payload,
    }),
    pushRates: (state, action: { payload: ratesModel[] }) => ({
      ...state,
      rates: action.payload,
    }),
    resetRates: (state) => ({
      ...state,
      rates: initialState.rates,
    }),
    pushSelectedDeadline: (state, action: { payload: string }) => ({
      ...state,
      selectedDeadline: action.payload,
    }),
    pushStatus: (state, action: { payload: orderStatus }) => ({
      ...state,
      status: action.payload,
    }),
    pushQR: (state, action: { payload: { qr: string; redirectUrl: string; orderId: string } }) => ({
      ...state,
      ...action.payload,
    }),
    pushFunnel: (state, action: { payload: funnelType[] }) => ({
      ...state,
      funnel: action.payload,
    }),
    pushGeolocation: (state, action: { payload: string }) => ({
      ...state,
      geolocation: action.payload,
    }),
    pushCommercialPolicies: (state, action: { payload: commercialPoliciesModel[] }) => ({
      ...state,
      commercialPolicies: action.payload,
    }),
    pushCommercialPolicyData: (state, action: { payload: { commercialPolicyId: number; articleId: number } }) => ({
      ...state,
      commercialPolicyId: action.payload.commercialPolicyId,
      articleId: action.payload.articleId,
    }),
    pushRejectionReason: (state, action: { payload: rejectionReasonType | null }) => ({
      ...state,
      rejectionReason: action.payload,
    }),
    resetNewOrderData: () => {
      return initialState;
    },
    pushSecondChance: (
      state,
      action: {
        payload: {
          applyBnpl: boolean;
          bnplApplyAmount: number;
          commercialPolicyId: number;
          articleId: number;
          description: string;
        };
      },
    ) => ({
      ...state,
      secondChance: { ...state.secondChance, ...action.payload },
    }),
    resetSecondChance: (state) => ({
      ...state,
      secondChance: initialState.secondChance,
    }),
    pushCampaignMessage: (state, action: { payload: string }) => ({
      ...state,
      campaignMessage: action.payload,
    }),
    resetCampaignMessage: (state) => ({
      ...state,
      campaignMessage: initialState.campaignMessage,
    }),
    resetRejectionReason: (state) => ({
      ...state,
      rejectionReason: initialState.rejectionReason,
    }),
  },
});

export const {
  pushTimestamp,
  pushOrderType,
  pushOrderData,
  pushClientData,
  pushPendingRequestId,
  pushClientOptions,
  pushMinimumAmount,
  pushMaximumAmount,
  pushRequestedAmount,
  pushRates,
  resetRates,
  pushSelectedDeadline,
  pushStatus,
  pushQR,
  pushFunnel,
  pushGeolocation,
  pushCommercialPolicies,
  pushCommercialPolicyData,
  resetNewOrderData,
  pushSecondChance,
  resetSecondChance,
  pushCampaignMessage,
  resetCampaignMessage,
  pushRejectionReason,
  resetRejectionReason,
} = newOrderSlice.actions;

export default newOrderSlice.reducer;
